import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Entity, TypeData } from '../../../model/entity.model';
import { EntityService } from '../../../services/entity.service';
import { Role } from '../../../model/role.model';
import { ClientService } from '../../../services/client.service';
import { RoleService } from '../../../services/role.service';
import { LoadingService } from '../../../loading.service';
import { FunctionService } from '../../../services/function.service';
import { DefaultValueEditorComponent } from '../default-value-editor/default-value-editor.component';
import { isReservedFieldName, reservedFieldNameMessage } from '../../../shared/entity/reserved-field-names.util';
import { referenceTargetOptions } from '../zs-entity/rules/entity-form-rules';

@Component({
    selector: 'app-entity',
    imports: [CommonModule, FormsModule, DefaultValueEditorComponent],
    templateUrl: './entity.component.html'
})
export class EntityComponent {

  booleanOptions = [
  { label: 'True', value: true },
  { label: 'False', value: false }
];


  @Input() entityData: Entity | TypeData = {
    name: '',
    type: 'TABLE',
    id: '',
    status: '',
    data: [
      {
        name: '',
        type: 'UUID',
        isUnique: false,
        primaryKey: false,
        startIncrementFrom: 0,
        referenceEntityName: '',
        referenceEntityColumnName: 'id',
        id: '',
        data: [],
        defaultValue: '',
      },
    ],
  };
  @Output() entityDataChange = new EventEmitter<string>();
  @Input() index: number = 0;
  @Input() modelNameInput: string = '';
  @Input() editFlag: boolean = false;
  @Output() exit = new EventEmitter<boolean>();

  modelName = '';
  status = '';
  entityList: Entity[] = [];

  dataTypeUniqueFlag = true;

  dataTypes = [
    'STRING',
    'NUMBER',
    'BOOLEAN',
    'ARRAY_OF_OBJECT',
    // 'AUTO_INCREMENT', — hidden 2026-09-17 (owner): a system column type, only for id_auto_increment.
    'ENTITY_REFERENCE',
    // 'SHARED_ENTITY_REFERENCE',
    'ATTACHMENT',
    'TIMESTAMP',
    'GEOMETRY'
  ];
  // if auto_increment we can have a variable which say from which number to start
  addVariableFlag = false;
  // Unique Key radio removed 2026-09-17 (owner) — the flag only drove that radio. Was:
  // newVariableUniqueFlag = true;
  addVariableButtonFlag = true;
  newVariableData: TypeData = {
    name: '',
    type: 'STRING',
    isUnique: false,
    primaryKey: false,
    startIncrementFrom: 0,
    referenceEntityName: '',
    referenceEntityColumnName: 'id',
    multiple: false,
    extendedAttributes: false,
    id: '',
    data: [],
    defaultValue: '',
    description: '',
  };

  roles: Role[] = [];
  rolePermissions: { [roleId: string]: { create: boolean; read: boolean; update: boolean; delete: boolean } } = {};
  selectedRolesForEntity: Role[] = []; // Roles that have been added to this entity for permission management
  hasUnsavedChanges: boolean = false;
  showAddRoleModal: boolean = false;
  /** Role ids that had a stored permission on THIS entity when we last loaded/saved. On save we diff
   *  this against the roles still in the grid: any that disappeared were removed by the user and must
   *  have their DB grant for this entity actively wiped. Without this, "removing" a role from an entity
   *  only clears the local map — the role keeps its stored access (a revoke that silently doesn't). */
  private persistedRoleIds: string[] = [];

  /**
   * Get permissions for a specific role and permission type
   */
  getRolePermission(roleId: string, permissionType: 'create' | 'read' | 'update' | 'delete'): boolean {
    return this.rolePermissions[roleId]?.[permissionType] ?? false;
  }

  /**
   * Toggle a specific permission for a role
   */
  toggleRolePermission(roleId: string, permissionType: 'create' | 'read' | 'update' | 'delete') {
    if (!this.rolePermissions[roleId]) {
      this.rolePermissions[roleId] = { create: false, read: false, update: false, delete: false };
    }
    this.rolePermissions[roleId][permissionType] = !this.rolePermissions[roleId][permissionType];
    this.hasUnsavedChanges = true;
  }

  /**
   * Check if any permission is enabled for a role (for highlighting the row)
   */
  hasAnyPermission(roleId: string): boolean {
    const perms = this.rolePermissions[roleId];
    return perms ? (perms.create || perms.read || perms.update || perms.delete) : false;
  }

  /**
   * Load existing permissions for this entity from all roles
   */
  async loadRolePermissions() {
    if (!('id' in this.entityData) || !this.entityData.id) return;
    
    const entityId = this.entityData.id;
    const permissions = await this.roleService.getEntityPermissionsAcrossRoles(entityId);
    
    // Initialize rolePermissions map and selectedRolesForEntity
    this.rolePermissions = {};
    this.selectedRolesForEntity = [];
    
    permissions.forEach(rolePermission => {
      if (rolePermission.permissions) {
        this.rolePermissions[rolePermission.roleId] = rolePermission.permissions;
        // Delete is not grantable to end-user roles (nothing is user-deletable; deletes + AAOB child
        // add/update/delete are system-handled). Force it off so any legacy delete grant is stripped
        // on the next save and never counts toward hasAnyPermission.
        this.rolePermissions[rolePermission.roleId].delete = false;
        // Add to selected roles if it has any permissions set
        const role = this.roles.find(r => r.id === rolePermission.roleId);
        if (role) {
          this.selectedRolesForEntity.push(role);
        }
      }
    });
    // Snapshot what's actually stored in the DB, so save() can tell which roles the user removed.
    this.persistedRoleIds = Object.keys(this.rolePermissions);
    this.hasUnsavedChanges = false;
  }

  /**
   * Toggle add role modal
   */
  toggleAddRoleModal() {
    this.showAddRoleModal = !this.showAddRoleModal;
  }

  /**
   * Check if role is already added to entity
   */
  isRoleAddedToEntity(roleId: string): boolean {
    return this.selectedRolesForEntity.some(r => r.id === roleId);
  }

  /**
   * Add role to entity for permission management
   */
  addRoleToEntity(role: Role) {
    if (!this.isRoleAddedToEntity(role.id)) {
      this.selectedRolesForEntity.push(role);
      // Initialize permissions if not already set
      if (!this.rolePermissions[role.id]) {
        this.rolePermissions[role.id] = { create: false, read: false, update: false, delete: false };
      }
      this.hasUnsavedChanges = true;
    }
    this.toggleAddRoleModal();
  }

  /**
   * Remove role from entity permission management
   */
  removeRoleFromEntity(roleId: string) {
    if (confirm('Remove this role from the entity? All permissions will be cleared.')) {
      this.selectedRolesForEntity = this.selectedRolesForEntity.filter(r => r.id !== roleId);
      // Clear permissions for this role
      if (this.rolePermissions[roleId]) {
        delete this.rolePermissions[roleId];
      }
      this.hasUnsavedChanges = true;
    }
  }

  /**
   * Get roles that haven't been added to this entity yet
   */
  getAvailableRoles(): Role[] {
    return this.roles.filter(role => !this.isRoleAddedToEntity(role.id));
  }
  trackByRoleId = (_: number, role: { id: string }) => role.id;

  /**
   * Save role permissions to the database
   */
  async saveRolePermissions() {
    if (!('id' in this.entityData) || !this.entityData.id) {
      alert('Please save the entity first before setting permissions');
      return;
    }

    const entityId = this.entityData.id;
    try {
      this.loading.isLoading = true;

      // Roles removed from the grid since load must have their DB grant for THIS entity wiped —
      // updateEntityPermissionsForMultipleRoles only writes roles still in the map, so a removed role
      // would otherwise keep its stored access. removeEntityPermissions drops this entity from that
      // role's grant set (leaving its other entities' grants intact).
      const currentRoleIds = Object.keys(this.rolePermissions);
      const removedRoleIds = this.persistedRoleIds.filter((id) => !currentRoleIds.includes(id));
      for (const roleId of removedRoleIds) {
        await this.roleService.removeEntityPermissions(roleId, entityId);
      }

      await this.roleService.updateEntityPermissionsForMultipleRoles(entityId, this.rolePermissions);

      this.persistedRoleIds = currentRoleIds; // snapshot now matches the DB
      this.hasUnsavedChanges = false;
      alert('✅ Permissions saved successfully!');
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('❌ Failed to save permissions. Please try again.');
    } finally {
      this.loading.isLoading = false;
    }
  }
  colors = ['border-red-700', 'border-green-500', 'border-indigo-500'];

  showExitWarning = false;

  constructor(
    public entityService: EntityService, // private adminService: OlympusAdminService,
    clientService: ClientService,
    private roleService: RoleService,
    private loading: LoadingService,
    private functionService: FunctionService
  ) {
    // Load roles from RoleService instead of client
    this.roleService.query(1000);
    this.roleService.$values.subscribe((values) => {
      this.roles = values || [];
    });
    
    // Only add escape key listener to the top-level entity component
    if (this.index === 0) {
      document.addEventListener('keydown', this.handleEscapeKey.bind(this));
    }
  }

  handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      // Prevent the event from bubbling up to parent components
      event.stopPropagation();
      
      if (this.addVariableFlag) {
        this.addVariableChangeFlag();
      } else if (this.index === 0) { // Only show exit warning for the root component
        this.showExitWarning = true;
      }
    }
  }

  ngOnInit() {
    if ('status' in this.entityData) {
      this.status = this.entityData.status;
      if (this.status == 'NEW') {
        this.editFlag = true;
      }
    }
    if (!this.index) {
      this.modelName = this.entityData.name;
      this.entityService.$values.subscribe((value: any) => {
        this.entityList = value;
        // this.clientDataList = this.convertToDataList(value);
      });
    } else {
      this.modelName = this.modelNameInput + '_' + this.entityData.name;
    }
    
    // Load role permissions for this entity
    if (this.index === 0) {
      this.loadRolePermissions();
    }
  }

  getColorClass(index: number): string {
    return this.colors[index % this.colors.length];
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('modelNameInput' in changes) {
      this.tableNameChanged();
    }
  }
  /**
   * True when THIS editor instance represents an ADVANCED (extendedAttributes) AOB. Such an AOB holds
   * dynamic, user-defined custom fields at runtime, so its design-time child set is frozen — no more
   * variables can be added to it. (entityData is Entity | TypeData; only a TypeData/AOB carries the flag.)
   */
  get isAdvancedAobContainer(): boolean {
    return !!(this.entityData as TypeData).extendedAttributes;
  }

  addVariableChangeFlag() {
    this.newVariableData = {
      name: '',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      extendedAttributes: false,
      id: '',
      data: [],
      defaultValue: '',
      description: '',
    };
    this.addVariableFlag = !this.addVariableFlag;
  }

  newVariableTypeChanged() {
    this.addVariableButtonFlag = true;
    this.newVariableData.data = [];

    // Advanced AOB-only flag — and only on a TOP-LEVEL AOB (index === 0). A nested AOB is never advanced.
    if (this.newVariableData.type !== 'ARRAY_OF_OBJECT') {
      this.newVariableData.extendedAttributes = false;
    } else {
      this.newVariableData.extendedAttributes = this.index === 0 ? !!this.newVariableData.extendedAttributes : false;
    }

    if (this.newVariableData.type == 'STRING') {
      this.newVariableData.defaultValue = '';
      // this.newVariableUniqueFlag = true;
    } else if (this.newVariableData.type == 'NUMBER') {
      this.newVariableData.defaultValue = 0;
    } else if (this.newVariableData.type == 'AUTO_INCREMENT') {
      this.newVariableData.startIncrementFrom = 0;
      this.newVariableData.defaultValue = 0;
      // this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'ARRAY_OF_STRING') {
      // this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'ARRAY_OF_NUMBER') {
      // this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'ARRAY_OF_OBJECT') {
      // this.newVariableUniqueFlag = false;
    } else if (this.newVariableData.type == 'BOOLEAN') {
      // this.newVariableUniqueFlag = false;
      this.newVariableData.defaultValue = false;
    } else if (this.newVariableData.type == 'TIMESTAMP') {
      // this.newVariableUniqueFlag = false;
      this.newVariableData.defaultValue = new Date().toISOString();
    } else if (this.newVariableData.type == 'GEOMETRY') {
      // Never unique: a jsonb geometry has no meaningful uniqueness constraint.
      // this.newVariableUniqueFlag = false;
      this.newVariableData.defaultValue = { type: 'Polygon', coordinates: [] };
    } else if (this.newVariableData.type == 'UUID') {
      // this.newVariableUniqueFlag = false;
      this.newVariableData.primaryKey = false;
      this.newVariableData.defaultValue = '';
    } else if (this.newVariableData.type == 'ATTACHMENT') {
      // this.newVariableUniqueFlag = false;
      this.newVariableData.primaryKey = false;
      this.newVariableData.defaultValue = '';
    }else if (this.newVariableData.type == 'ENTITY_REFERENCE') {
      // this.newVariableUniqueFlag = false;
      this.addVariableButtonFlag = false;
      this.newVariableData.referenceEntityName = '';
      this.newVariableData.referenceEntityColumnName = 'id';
    } 
    // else if (this.newVariableData.type == 'SHARED_ENTITY_REFERENCE') {
    //   this.newVariableUniqueFlag = false;
    //   this.addVariableButtonFlag = false;
    //   this.newVariableData.referenceEntityName = '';
    //   this.newVariableData.referenceEntityColumnName = '';
    //   this.newVariableData.defaultValue = null;
    // } 
    else {
      // this.newVariableUniqueFlag = true;
    }
  }
  newVariableReferenceEntityChanged() {
    if (this.newVariableData.referenceEntityName !== '') {
      this.addVariableButtonFlag = true;
    }
  }
  serTargetEntityChanged() {
    // For SER, only enable button when both entity and column name are provided
    if (this.newVariableData.referenceEntityName !== '' && 
        this.newVariableData.referenceEntityColumnName !== '') {
      this.addVariableButtonFlag = true;
    } else {
      this.addVariableButtonFlag = false;
    }
  }
  serReverseColumnNameChanged() {
    // Validate column name (alphanumeric and underscore only)
    this.newVariableData.referenceEntityColumnName = 
      this.newVariableData.referenceEntityColumnName.replace(/[^a-zA-Z0-9_]/g, '');
    
    // Enable button only if both fields are filled
    if (this.newVariableData.referenceEntityName !== '' && 
        this.newVariableData.referenceEntityColumnName !== '') {
      this.addVariableButtonFlag = true;
    } else {
      this.addVariableButtonFlag = false;
    }
  }
  getTargetEntityName(): string {
    if (!this.newVariableData.referenceEntityName) {
      return 'target entity';
    }
    const targetEntity = this.entityService.completeFinalizedEntityList.find(
      e => e.id === this.newVariableData.referenceEntityName
    );
    return targetEntity ? targetEntity.name : 'target entity';
  }
  
  getEntityNameById(entityId: string): string {
    if (!entityId) {
      return entityId;
    }
    const entity = this.entityService.completeFinalizedEntityList.find(
      e => e.id === entityId
    );
    return entity ? entity.name : entityId;
  }
  // validations
  newVaraibleStartIncementChange() {
    if (this.newVariableData.startIncrementFrom < 0) {
      this.newVariableData.startIncrementFrom = 0;
    }
  }
  addVariable() {
    this.newVariableData.name = this.newVariableData.name.replace(/[^a-zA-Z]/g, '');
    if (this.newVariableData.name.toLowerCase().includes('system')) {
      alert('Variable name cannot contain the word "system"');
      return;
    }
    if (isReservedFieldName(this.newVariableData.name)) {
      alert(reservedFieldNameMessage(this.newVariableData.name));
      return;
    }
    this.newVariableData.id =
      this.newVariableData.name + '_' + this.generateTableId();
    var flag = this.uniqueVariableNameCheck();
    if (flag) {
      if (this.newVariableData.type == 'ARRAY_OF_OBJECT') {
        // Advanced is allowed ONLY on a top-level AOB (index === 0); a nested AOB is never advanced.
        this.newVariableData.extendedAttributes = this.index === 0 ? !!this.newVariableData.extendedAttributes : false;
        this.newVariableData.data = [
          {
            name: 'id',
            type: 'UUID',
            isUnique: true,
            primaryKey: true,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: '',
          },
          {
            name: 'id_auto_increment',
            type: 'AUTO_INCREMENT',
            isUnique: true,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: '',
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: 0,
          },
          {
            name: 'parent_id',
            type: 'ENTITY_REFERENCE',
            isUnique: true,
            primaryKey: false,
            startIncrementFrom: 0,
            referenceEntityName: this.entityData.id,
            referenceEntityColumnName: 'id',
            id: '',
            data: [],
            defaultValue: '',
          },
        ];

        if (this.newVariableData.extendedAttributes) {
          this.newVariableData.data.push(
            {
              name: 'systemKey',
              type: 'STRING',
              isUnique: false,
              primaryKey: false,
              startIncrementFrom: 0,
              referenceEntityName: '',
              referenceEntityColumnName: 'id',
              id: '',
              data: [],
              defaultValue: '',
            },
            {
              name: 'systemType',
              type: 'STRING',
              isUnique: false,
              primaryKey: false,
              startIncrementFrom: 0,
              referenceEntityName: '',
              referenceEntityColumnName: 'id',
              id: '',
              data: [],
              defaultValue: '',
            },
            {
              name: 'systemString',
              type: 'STRING',
              isUnique: false,
              primaryKey: false,
              startIncrementFrom: 0,
              referenceEntityName: '',
              referenceEntityColumnName: 'id',
              id: '',
              data: [],
              defaultValue: '',
            },
            {
              name: 'systemNumber',
              type: 'NUMBER',
              isUnique: false,
              primaryKey: false,
              startIncrementFrom: 0,
              referenceEntityName: '',
              referenceEntityColumnName: 'id',
              id: '',
              data: [],
              defaultValue: 0,
            },
            {
              name: 'systemBoolean',
              type: 'BOOLEAN',
              isUnique: false,
              primaryKey: false,
              startIncrementFrom: 0,
              referenceEntityName: '',
              referenceEntityColumnName: 'id',
              id: '',
              data: [],
              defaultValue: false,
            },
            {
              name: 'systemTimestamp',
              type: 'TIMESTAMP',
              isUnique: false,
              primaryKey: false,
              startIncrementFrom: 0,
              referenceEntityName: '',
              referenceEntityColumnName: 'id',
              id: '',
              data: [],
              defaultValue: new Date().toISOString(),
            },
          );
        }
      }
      this.entityData.data.push(
        JSON.parse(JSON.stringify(this.newVariableData))
      );
      this.newVariableData = {
        name: '',
        type: 'STRING',
        isUnique: false,
        primaryKey: false,
        startIncrementFrom: 0,
        referenceEntityName: '',
        referenceEntityColumnName: 'id',
        extendedAttributes: false,
        id: '',
        data: [],
        defaultValue: '',
        description: '',
      };
      this.addVariableFlag = !this.addVariableFlag;
    } else {
      alert('same variable name already exists, please change');
    }
  }

  uniqueVariableNameCheck() {
    if (this.newVariableData.name == '') {
      return false;
    }
    for (let i = 0; i < this.entityData.data.length; i++) {
      if (this.entityData.data[i].name == this.newVariableData.name) {
        return false;
      }
    }
    return true;
  }
  // check table name
  uniqueTableNameCheck() {
    if (this.entityData.name == '') {
      return false;
    }
    for (let i = 0; i < this.entityList.length; i++) {
      if (this.entityData.id == this.entityList[i].id) {
        return false;
      }
    }
    return true;
  }

  variableNameChanged() {
    this.newVariableData.name = this.newVariableData.name.replace(
      /[^a-zA-Z_]/g,
      ''
    );
    this.newVariableData.name = this.newVariableData.name.replace(/system/gi, '');
  }
  tableNameChanged() {
    this.entityData.name = this.entityData.name.replace(/[^a-zA-Z0-9]/g, '');
    this.entityData.name = this.entityData.name.replace(/system/gi, '');
    this.entityData.name = this.entityData.name.replace(/ootb/gi, '');
    if (!this.index) {
      this.modelName = this.entityData.name;
    } else {
      this.modelName = this.modelNameInput + '_' + this.entityData.name;
    }

    // this.changeForigenKeyName();
  }
  //   changeForigenKeyName() {
  //     for(let i=0; i < this.entityData.data.length; i ++) {
  // if(this.entityData.data[i].name == 'parent_id') {
  // this.entityData.data[i].referenceEntityName = this.modelNameInput;
  // }
  //     }
  //   }
  SaveTableChanges(finalizeFlag: boolean = false) {
    if ('id' in this.entityData) {
      var flag = true;
      if (this.entityData.id == '') {
        this.entityData.name = this.entityData.name.replace(
          /[^a-zA-Z0-9_]/g,
          ''
        );
        
        // Validate entity name doesn't contain 'system'
        if (this.entityData.name.toLowerCase().includes('system')) {
          alert('Entity name cannot contain the word "system"');
          return;
        }
        
        this.entityData.id =
          this.entityData.name + '_' + this.generateTableId();
        flag = this.uniqueTableNameCheck();
      }
      for (let j = 0; j < this.entityData.data.length; j++) {
        if (this.entityData.data[j].type == 'ARRAY_OF_OBJECT') {
          const parentIdField = this.entityData.data[j].data?.find(
            (f: any) => f.name === 'parent_id'
          );
          if (parentIdField) {
            parentIdField.referenceEntityName = this.entityData.id;
          }
        }
      }
      if (flag) {
        try {
          if ('status' in this.entityData) {
            console.log('status', this.entityData);
            this.dowWithLoadingScreen(async () => {
              await this.entityService.create(this.entityData as Entity);
              // Save role permissions if there are any changes
              if (this.hasUnsavedChanges && this.entityData.id) {
                await this.roleService.updateEntityPermissionsForMultipleRoles(
                  this.entityData.id,
                  this.rolePermissions
                );
                this.hasUnsavedChanges = false;
              }
            }).catch((error) => {
              // updateEntityPermissionsForMultipleRoles now throws on a failed write (it used to
              // report success either way) — this call is intentionally fire-and-forget (the
              // entityList update + exit.emit below don't wait on it), so an uncaught rejection
              // here would surface only as an unhandled promise rejection. Surface it the way
              // saveRolePermissions does instead.
              console.error('Error updating entity:', error);
              alert('Failed to save changes. Please try again.');
            });

            for (let i = 0; i < this.entityList.length; i++) {
              if (this.entityList[i].id == this.entityData.id) {
                this.entityList[i] = JSON.parse(
                  JSON.stringify(this.entityData)
                );
                break;
              }
            }
            if (!finalizeFlag) {
              this.exit.emit(true);
            }
          }
        } catch (error) {
          console.log('error', error);
        }
      } else {
        alert('same Data Model name already exists, please change');
      }
      return flag;
    } else {
      return false;
    }
  }

  generateTableId(): string {
    const randomInt = Math.floor(Math.random() * 10000000000000);
    const timestamp = Date.now();
    const combinedValue = randomInt + timestamp; // Combine random number and timestamp
    const base64String = btoa(String(combinedValue));
    return base64String.substring(0, 6); // Extract first 6 characters
  }
  async FinalizeTableChanges() {
    var flag = this.SaveTableChanges(true);
    if (flag) {
      try {
        // await this.adminService.createEntity(this.entityData as Entity);
        // ! if the responsecis a error the next code should not run;
        if ('status' in this.entityData) {
          // Pending until the table queue builds it; only Zavo marks it Ready (functions ADR 0005).
          this.entityData.status = 'PENDING';
          this.SaveTableChanges(false);
        }
      } catch (error) {}
    }
  }
  async LocalTableChanges() {
    // NOTE: this app-entity editor is unused — the live entity editor is entity-list.component, where
    // the LOCAL scalar-only guard + predefined rows now live. Kept commented for reference:
    // const ed: any = this.entityData;
    // const blocked = (ed.data || []).filter((f: any) => f.type === 'ENTITY_REFERENCE' || f.type === 'ARRAY_OF_OBJECT');
    // if (blocked.length) {
    //   alert("A LOCAL entity can't have reference or nested-list (AOB) fields: " + blocked.map((f: any) => f.name).join(', ') + '. Remove them before keeping it local.');
    //   return;
    // }
    var flag = this.SaveTableChanges(true);
    if (flag) {
      try {
        // await this.adminService.createEntity(this.entityData as Entity);
        // ! if the responsecis a error the next code should not run;
        if ('status' in this.entityData) {
          this.entityData.status = 'LOCAL';
          this.SaveTableChanges(false);
        }
      } catch (error) {}
    }
  }

  /** The reference picker's choices: the open entity first as "(this entity)", then the rest. Inside a list field `entityData` is the list's own TypeData, whose id matches no entity, so a list row pointing at its owner is not "this entity". */
  get referenceTargetChoices(): { name: string; id: string; label: string }[] {
    return referenceTargetOptions(this.entityService.completeFinalizedEntityList as any, (this.entityData as any)?.id ?? '');
  }

  /* MOVED TO entity-list.component (this app-entity editor is unused). Kept commented for reference:
  // Unfinalized entities (NEW + LOCAL) are scalar-only (for now): drop reference / nested-list (AOB)
  // from the add-field type options. They remain available only when editing a finalized entity.
  get availableDataTypes(): string[] {
    if (this.status === 'LOCAL' || this.status === 'NEW') return this.dataTypes.filter((t) => t !== 'ENTITY_REFERENCE' && t !== 'ARRAY_OF_OBJECT');
    return this.dataTypes;
  }
  // ── Predefined rows (LOCAL entities) ── seed rows persisted on entityData.predefinedData; runtime
  // auto-fills entity-list vars from these (ui-editor seedPredefinedData). Scalar, non-system fields.
  isScalarFieldType(t: string): boolean {
    return t === 'STRING' || t === 'NUMBER' || t === 'BOOLEAN' || t === 'TIMESTAMP' || t === 'GEOMETRY';
  }
  get predefinedFields(): any[] {
    const ed: any = this.entityData;
    return (ed.data || []).filter(
      (f: any) => this.isScalarFieldType(f.type) && f.name !== 'id' && f.name !== 'id_auto_increment' && f.name !== 'parent_id'
    );
  }
  get predefinedRows(): any[] {
    const ed: any = this.entityData;
    if (!Array.isArray(ed.predefinedData)) ed.predefinedData = [];
    return ed.predefinedData;
  }
  addPredefinedRow(): void {
    const ed: any = this.entityData;
    if (!Array.isArray(ed.predefinedData)) ed.predefinedData = [];
    const row: any = {};
    for (const f of this.predefinedFields) {
      row[f.name] = f.defaultValue !== undefined && f.defaultValue !== '' ? f.defaultValue : f.type === 'NUMBER' ? 0 : f.type === 'BOOLEAN' ? false : '';
    }
    ed.predefinedData.push(row);
  }
  removePredefinedRow(i: number): void {
    const ed: any = this.entityData;
    if (Array.isArray(ed.predefinedData)) ed.predefinedData.splice(i, 1);
  }
  */

 dowWithLoadingScreen = async (expression :Function) => {
    try {
      this.loading.isLoading = true
      await expression();
    } finally {
      this.loading.isLoading = false
    }
  }

  async UpdateTableChanges() {
    await this.dowWithLoadingScreen(async () => {
      try {
        await this.entityService.create({
          ...this.entityData,
          updatedBySystem: false,
        } as Entity);
        // Save role permissions if there are any changes
        if (this.hasUnsavedChanges && 'id' in this.entityData && this.entityData.id) {
          await this.roleService.updateEntityPermissionsForMultipleRoles(
            this.entityData.id,
            this.rolePermissions
          );
          this.hasUnsavedChanges = false;
        }
      } catch (error) {
        // updateEntityPermissionsForMultipleRoles now throws on a failed write (it used to report
        // success either way). exit.emit was the last statement in this callback, so an uncaught
        // throw here used to mean the panel's completion signal never fired. Surface the failure
        // the way saveRolePermissions does, and always emit — success or failure — in `finally`.
        console.error('Error updating entity:', error);
        alert('Failed to save changes. Please try again.');
      } finally {
        this.exit.emit(false);
      }
    });
  }

  deleteVariable(index: number) {
    this.entityData.data.splice(index, 1);
  }
  // Add this property at the top of the class with other properties
  editingVariableIndex: number | null = null;

  editVariable(index: number) {
    // Store the index of variable being edited
    this.editingVariableIndex = index;
    
    // Copy the variable data to the form
    this.newVariableData = JSON.parse(JSON.stringify(this.entityData.data[index]));
    
    // Set appropriate flags based on variable type
    if (this.newVariableData.type === 'STRING' || this.newVariableData.type === 'NUMBER'|| this.newVariableData.type === 'BOOLEAN') {
      // this.newVariableUniqueFlag = true;
    } else {
      // this.newVariableUniqueFlag = false;
    }
    
    // Show the edit dialog
    this.addVariableFlag = true;
    this.addVariableButtonFlag = true;
  }

  updateVariable() {
    if (this.editingVariableIndex === null) return;
    
    const currentVar = this.entityData.data[this.editingVariableIndex];
    
    // If editFlag is true, allow editing all properties
    if (this.editFlag) {
      this.newVariableData.name = this.newVariableData.name.replace(/[^a-zA-Z_]/g, '');

      if (this.newVariableData.name.toLowerCase().includes('system')) {
        alert('Variable name cannot contain the word "system"');
        return;
      }

      // Check if name is unique (unless it's the same as the original name)
      if (this.newVariableData.name !== currentVar.name) {
        if (isReservedFieldName(this.newVariableData.name)) {
          alert(reservedFieldNameMessage(this.newVariableData.name));
          return;
        }
        const nameExists = this.entityData.data.some(
          (item, idx) => item.name === this.newVariableData.name && idx !== this.editingVariableIndex
        );
        if (nameExists) {
          alert('A variable with this name already exists');
          return;
        }
      }
      
      // Update all properties
      this.entityData.data[this.editingVariableIndex] = JSON.parse(JSON.stringify(this.newVariableData));
    } else {
      // If editFlag is false, only allow updating default value
      currentVar.defaultValue = this.newVariableData.defaultValue;
    }
    
    // Reset and close modal
    this.editingVariableIndex = null;
    this.newVariableData = {
      name: '',
      type: 'STRING',
      isUnique: false,
      primaryKey: false,
      startIncrementFrom: 0,
      referenceEntityName: '',
      referenceEntityColumnName: 'id',
      extendedAttributes: false,
      id: '',
      data: [],
      defaultValue: '',
      description: '',
    };
    this.addVariableFlag = false;
  }

  checkDeleteFalgForObject(index: number) {
    var flag = true;
    if (this.entityData.data[index].type == 'ARRAY_OF_OBJECT') {
      for (let i = 0; i < this.entityData.data[index].data.length; i++) {
        if (this.entityData.data[index].data[i].type == 'ARRAY_OF_OBJECT') {
          return false;
        }
      }
    }
    return flag;
  }

  exitToEntityList() {
    this.exit.emit(true);
  }

  confirmExit() {
    this.showExitWarning = false;
    this.exitToEntityList();
  }

  cancelExit(event?: MouseEvent) {
    if (event) {
      // Prevent the event from bubbling up to parent components
      event.stopPropagation();
    }
    this.showExitWarning = false;
  }

  hasValidId(): boolean {
    if (!('id' in this.entityData)) return true;
    if ('id' in this.entityData && this.entityData.id == '') return false;
    else return true;
  }

  ngOnDestroy() {
    if (this.index === 0) {
      document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
    }
  }
}
